import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { STATIC_PAGES_ROUTES } from './features/static-pages/static-pages.routes';
import { BrandingService } from './core/services';

export const routes: Routes = [
  {
    // On a branded (white-label) host in PORTFOLIO mode the root renders a
    // branded hub listing all of the owner's offers, keeping the clean URL
    // (https://customer-domain/). Checked BEFORE the single-offer branch.
    path: '',
    pathMatch: 'full',
    canMatch: [
      () => {
        const branding = inject(BrandingService);
        return branding.isWhiteLabel() && branding.isPortfolio();
      },
    ],
    loadComponent: () =>
      import('./features/white-label-portfolio/pages/portfolio-landing/portfolio-landing.component')
        .then(m => m.PortfolioLandingComponent),
  },
  {
    // On a branded (white-label) host the root renders that offer IN PLACE,
    // keeping the clean URL (https://customer-domain/ — no /property/<slug>).
    // The slug comes from the resolved branding, not the route.
    path: '',
    pathMatch: 'full',
    canMatch: [
      () => {
        const branding = inject(BrandingService);
        return branding.isWhiteLabel() && !!branding.slug();
      },
    ],
    loadComponent: () =>
      import('./features/property-detail/pages/property-detail-page/property-detail-page.component')
        .then(m => m.PropertyDetailPageComponent),
  },
  {
    // Main host (or an unresolved host): root goes to the properties list.
    path: '',
    pathMatch: 'full',
    redirectTo: '/properties',
  },
  {
    path: 'properties',
    loadChildren: () =>
      import('./features/properties/properties.routes').then(m => m.PROPERTIES_ROUTES)
  },
  {
    path: 'favorites',
    loadChildren: () =>
      import('./features/favorites/favorites.routes').then(m => m.FAVORITES_ROUTES)
  },
  {
    path: 'property/:slug',
    loadChildren: () =>
      import('./features/property-detail/property-detail.routes').then(m => m.PROPERTY_DETAIL_ROUTES)
  },
  {
    path: 'inquiry/reply',
    loadChildren: () =>
      import('./features/inquiry-chat/inquiry-chat.routes').then(m => m.INQUIRY_CHAT_ROUTES),
  },
  {
    path: 'my-conversations',
    loadChildren: () =>
      import('./features/my-conversations/my-conversations.routes').then(m => m.MY_CONVERSATIONS_ROUTES),
  },
  {
    path: 'my-data',
    loadChildren: () =>
      import('./features/privacy-center/privacy-center.routes').then(m => m.PRIVACY_CENTER_ROUTES),
  },
  {
    // Embeddable availability-calendar widget rendered inside a customer-site
    // iframe (loader: public/widget/v1/embed.js). No app layout, no guards —
    // works on the main host; keep this chunk free of Leaflet/heavy features.
    path: 'embed/:key',
    loadComponent: () =>
      import('./features/embed-widget/pages/embed-page/embed-page.component')
        .then(m => m.EmbedPageComponent),
  },
  {
    // Public guest-info page opened from a QR code in the property
    // (https://zaklepuj.pl/gosc/<token>). No app layout, no guards, noindex —
    // keep this chunk as light as the embed widget.
    path: 'gosc/:token',
    loadComponent: () =>
      import('./features/guest-info/pages/guest-page/guest-page.component')
        .then(m => m.GuestPageComponent),
  },
  {
    // English alias of gosc/:token — same standalone page.
    path: 'guest/:token',
    loadComponent: () =>
      import('./features/guest-info/pages/guest-page/guest-page.component')
        .then(m => m.GuestPageComponent),
  },
  {
    // Public request board („Szukam noclegu”): board, new request, e-mail
    // verification and request detail. Top-level so /properties/:slug does
    // not swallow it; lazy so the search bundle stays untouched.
    path: 'szukam-noclegu',
    // Main brand only — white-label hosts never load (or render) the board.
    canMatch: [
      () => {
        const branding = inject(BrandingService);
        return branding.isWhiteLabel() ? inject(Router).createUrlTree(['/']) : true;
      },
    ],
    loadChildren: () =>
      import('./features/request-board/request-board.routes').then(m => m.REQUEST_BOARD_ROUTES),
  },
  ...STATIC_PAGES_ROUTES,
];
